import _ from 'lodash';
import moment from 'moment';
import { Dayparts, DaypartsDay } from 'core/dayparts/Dayparts';
import { DeviceDaypartsParam } from 'core/deviceManagement/DeviceManagement';

/** 1 是週一、7 是週日，與後端的 key 一致。 */
export const DAYPART_DAYS: DaypartsDay[] = [1, 2, 3, 4, 5, 6, 7];

/**
 * 換成後端收得到的形狀。
 *
 * `Dayparts` 這個型別是從活動設定沿用的，它多帶一個畫面用的 `enabled` 旗標；
 * 後端對非數字的 key 顯式拒絕，留著它整包請求會回 400。
 *
 * 七天一律都送，就算那天一格都沒選也送一個空陣列。後端把空物件 `{}` 當成全天，
 * 所以「全部沒選」不能靠省略 key 來表達 —— 省到最後就是一個空物件，意思剛好相反。
 */
export function toDaypartsParam(daypart: Dayparts): DeviceDaypartsParam {
  return DAYPART_DAYS.reduce<DeviceDaypartsParam>((param, day) => {
    param[day] = _.sortBy(daypart[day] ?? []);
    return param;
  }, {});
}

/**
 * 這份時段表一週總共選了幾個小時。
 *
 * 一格就是一小時，所以直接數格子；`enabled` 是畫面用的旗標，不在七天的 key 裡，
 * 不會被數進來。走期會有幾天不影響這個數字，它問的是「一週」。
 */
export function countSelectedHours(daypart: Dayparts): number {
  return DAYPART_DAYS.reduce(
    (total, day) => total + (daypart[day] ?? []).length,
    0
  );
}

/**
 * 某一天對應到時段表的哪一列。
 *
 * `isoWeekday` 的 1 是週一、7 是週日，與後端的 key 剛好同一套編號，
 * 不要改用 `day()`（那是 0 起算的週日）。
 */
export function daypartDayOfDate(date: string): DaypartsDay {
  return moment(date, 'YYYY-MM-DD').isoWeekday() as DaypartsDay;
}

/** 這一天有沒有落在投放時段內。 */
export function hasScheduledHoursOnDate(
  daypart: Dayparts,
  date: string
): boolean {
  return (daypart[daypartDayOfDate(date)] ?? []).length > 0;
}

export enum DaypartPreset {
  ALL = 'all',
  NONE = 'none',
  COMMUTE = 'commute',
  WEEKEND = 'weekend'
}

export const DAYPART_PRESET_I18N: Record<DaypartPreset, string> = {
  [DaypartPreset.ALL]: 'deviceManagement.buttons.daypartAll',
  [DaypartPreset.NONE]: 'deviceManagement.buttons.daypartNone',
  [DaypartPreset.COMMUTE]: 'deviceManagement.buttons.daypartCommute',
  [DaypartPreset.WEEKEND]: 'deviceManagement.buttons.daypartWeekend'
};

const ALL_HOURS = Array.from({ length: 24 }, (_unused, hour) => hour);

/** 平日通勤:週一到週五的 07–09 與 17–20 點。 */
const COMMUTE_HOURS = [7, 8, 9, 17, 18, 19, 20];

/**
 * 四個快捷各自產生的整張時段表。
 *
 * 七天逐一列出而不是「全選再扣掉」：這張表是七個獨立的陣列，用扣的會把沒被扣到的
 * 那幾天留成上一次的內容。
 */
export function daypartPreset(preset: DaypartPreset): Dayparts {
  return DAYPART_DAYS.reduce(
    (daypart, day) => {
      const isWeekday = day <= 5;
      let hours: number[] = [];
      if (preset === DaypartPreset.ALL) {
        hours = ALL_HOURS;
      } else if (preset === DaypartPreset.COMMUTE && isWeekday) {
        hours = COMMUTE_HOURS;
      } else if (preset === DaypartPreset.WEEKEND && !isWeekday) {
        hours = ALL_HOURS;
      }
      return { ...daypart, [day]: [...hours] };
    },
    { enabled: true } as Dayparts
  );
}
